import type { AddressCheck, MobileAddress, OnlineClinic, OnlineConfirmation, OnlineEvent, OnlineEventDay, OnlineMe, OnlinePerson, OnlineSlots, ResolvedAddress } from "@vaulted/shared";
import { CONSENT_CHANNELS, GUARDIAN_WORDING, ONLINE_CONSENT_WORDING, ageOn, depositFor, formatCents, formatPhone, visitConflicts } from "@vaulted/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import QRCode from "qrcode";
import { type CSSProperties, type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { trackStartedBooking, trackingCookies, useMarketingTags } from "../marketing";
import { Link, useLocation, useParams, useSearchParams } from "react-router";
import { ApiError, errorMessage, get, post } from "../api";
import { time12 } from "../components/AppointmentDialog";
import { StripeCheckout } from "../components/StripeCheckout";
import { DateOfBirthField, Alert, Button, Checkbox, Field, Loading, SelectField, cx } from "../components/ui";

type Step = "start" | "home" | "where" | "address" | "category" | "plans" | "service" | "who" | "time" | "phone" | "code" | "pick-me" | "about" | "review" | "done";
type Context = "clinic" | "mobile" | "event";
const STEPS: Step[] = ["start", "home", "where", "address", "category", "plans", "service", "who", "time", "phone", "code", "pick-me", "about", "review", "done"];

interface Person {
  firstName: string;
  lastName: string;
  dob: string;
  email: string | null;
  mobile: string | null;
  guardian: boolean;
  /** Adults: link them to the booker as family, so they're one tap next time. */
  saveAsFamily: boolean;
}

interface Guest {
  key: string;
  /** "me": whoever is booking (known once they sign in); "client": someone on their account; "new": typed in. */
  who: "me" | "client" | "new";
  clientId: string | null;
  person: Person | null;
  label: string;
  /** As many services as they like, each with its add-ons. */
  services: { serviceId: string; addOnIds: string[] }[];
  staffId: string | null;
}

const one = (serviceId: string) => (serviceId ? [{ serviceId, addOnIds: [] }] : []);
const meGuest = (services: Guest["services"] = []): Guest => ({ key: "me", who: "me", clientId: null, person: null, label: "You", services, staffId: null });
const clientGuest = (p: Pick<OnlinePerson, "id" | "firstName">, services: Guest["services"] = []): Guest => ({ key: p.id, who: "client", clientId: p.id, person: null, label: p.firstName, services, staffId: null });

/** What can be booked here, at what price: the clinic or mobile menu, or an event's. */
interface Menu {
  services: { id: string; name: string; description: string; category: string; priceCents: number; minutes: number; includedAddOnIds: string[]; addOnIds: string[]; onePerVisit: boolean; medicationIds: string[] }[];
  addOns: { id: string; name: string; description: string; priceCents: number; medicationIds: string[] }[];
  groupDiscount: string | null;
}

interface Place {
  address: MobileAddress;
  label: string;
  check: AddressCheck;
  locationType: string;
  /** They ticked the place type's note, and (outside the area) that it may be declined. */
  noticeAgreed: boolean;
  outsideAgreed: boolean;
}

const addDays = (date: string, n: number) => {
  const d = new Date(`${date}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};
const dateLabel = (d: string, opts: Intl.DateTimeFormatOptions) => new Date(`${d}T12:00:00Z`).toLocaleDateString(undefined, { ...opts, timeZone: "UTC" });
const todayIso = () => new Date().toISOString().slice(0, 10);
const isMinor = (dob: string) => !!dob && ageOn(dob, todayIso()) < 18;

function menuFor(clinic: OnlineClinic, context: Context, event: OnlineEvent | null): Menu {
  if (context === "event" && event) {
    return { services: event.services.map((s) => ({ ...s, category: "" })), addOns: event.addOns, groupDiscount: null };
  }
  if (context === "mobile") {
    const addOns = clinic.addOns.filter((a) => a.mobilePriceCents != null).map((a) => ({ ...a, priceCents: a.mobilePriceCents! }));
    return {
      services: clinic.services
        .filter((s) => s.mobilePriceCents != null)
        .map((s) => ({ ...s, priceCents: s.mobilePriceCents!, addOnIds: s.addOnIds.filter((id) => addOns.some((a) => a.id === id)) })),
      addOns,
      groupDiscount: clinic.mobile?.groupDiscount ?? null,
    };
  }
  const addOns = clinic.addOns.filter((a) => a.clinic);
  return {
    services: clinic.services.filter((s) => s.clinic).map((s) => ({ ...s, addOnIds: s.addOnIds.filter((id) => addOns.some((a) => a.id === id)) })),
    addOns,
    groupDiscount: clinic.groupDiscount,
  };
}

/** Would these services break the visit rules (two drips, or a medication twice)? Same rules the server enforces. */
function breaksRules(menu: Menu, services: Guest["services"]) {
  const meds = (ids: string[]) => ids.map((id) => ({ id, name: id }));
  const lines = services.flatMap((sv) => {
    const s = menu.services.find((x) => x.id === sv.serviceId);
    if (!s) return [];
    return [
      {
        service: { id: s.id, name: s.name, includedAddOnIds: s.includedAddOnIds, medications: meds(s.medicationIds), onePerVisitCategory: s.onePerVisit ? s.category || "drips" : null },
        addOns: sv.addOnIds.flatMap((id) => {
          const a = menu.addOns.find((x) => x.id === id);
          return a ? [{ id: a.id, name: a.name, medications: meds(a.medicationIds) }] : [];
        }),
      },
    ];
  });
  return visitConflicts(lines).length > 0;
}

/** A rough total while they pick (the review step shows the real one, with discounts and memberships). */
function estimate(menu: Menu, guests: Guest[]) {
  return guests.reduce(
    (t, g) =>
      t +
      g.services.reduce((u, sv) => {
        const s = menu.services.find((x) => x.id === sv.serviceId);
        return u + (s?.priceCents ?? 0) + sv.addOnIds.reduce((w, id) => w + (s?.includedAddOnIds.includes(id) ? 0 : (menu.addOns.find((a) => a.id === id)?.priceCents ?? 0)), 0);
      }, 0),
    0,
  );
}

/** Picks kept on this device for a day, so closing the tab doesn't lose them. Nothing personal is kept. */
interface Saved {
  at: number;
  context: Context;
  services: Guest["services"];
}
const savedKey = (slug: string) => `vaulted.book.${slug}`;
/**
 * Open on the clinic's lobby display (in its frame): nothing is remembered on the device, no
 * "remember this phone", no browser autofill, and the lobby is told when the booking's done.
 */
export const inLobby = (() => {
  try {
    return window.top !== window.self && window.top!.location.pathname.startsWith("/device/");
  } catch {
    return false;
  }
})();
function loadSaved(slug: string): Saved | null {
  if (inLobby) return null;
  try {
    const v = JSON.parse(localStorage.getItem(savedKey(slug)) ?? "null") as Saved | null;
    return v && Date.now() - v.at < 86_400_000 ? v : null;
  } catch {
    return null;
  }
}
function save(slug: string, v: Saved | null) {
  if (inLobby) return;
  try {
    if (v) localStorage.setItem(savedKey(slug), JSON.stringify(v));
    else localStorage.removeItem(savedKey(slug));
  } catch {
    // Private windows can refuse storage; booking still works.
  }
}

/**
 * Online booking for clients. No staff sign-in; clients sign in with a texted code.
 *  /book/<clinic>            clinic, or mobile when offered ("Where?")
 *  /book/<clinic>/mobile     mobile visits
 *  /book/<clinic>/e/<event>  an event's own page
 *
 * One step per screen, each with its own address (?step=…), so Back works. Existing clients sign in
 * first and land on their page; new clients pick what and when, then sign in at the end.
 */
/** On the lobby display, the browser never fills in (or offers) the last person's details. */
export function useLobbyNoAutofill() {
  useEffect(() => {
    if (!inLobby) return;
    const off = () => document.querySelectorAll("input, textarea").forEach((el) => el.setAttribute("autocomplete", "off"));
    off();
    const watch = new MutationObserver(off);
    watch.observe(document.body, { childList: true, subtree: true });
    return () => watch.disconnect();
  }, []);
}

export function BookPage() {
  useLobbyNoAutofill();
  const { slug = "", event: eventSlug } = useParams();
  const fixedMobile = useLocation().pathname.replace(/\/$/, "").endsWith("/mobile");
  const [search, setSearch] = useSearchParams();
  const preFor = search.get("for");
  const preService = search.get("service");
  const rawStep = search.get("step") as Step | null;
  const step: Step = rawStep && STEPS.includes(rawStep) ? rawStep : "start";
  const go = (s: Step, replace = false) =>
    setSearch(
      (cur) => {
        const n = new URLSearchParams(cur);
        n.set("step", s);
        return n;
      },
      { replace },
    );
  const base = `/api/book/${slug}`;
  const qc = useQueryClient();
  const page = useQuery({ queryKey: ["book", slug], queryFn: () => get<OnlineClinic>(base), retry: false });
  const eventPage = useQuery({ queryKey: ["book-event", slug, eventSlug], queryFn: () => get<OnlineEvent>(`${base}/e/${eventSlug}`), retry: false, enabled: !!eventSlug });
  const [flow, setFlow] = useState<"new" | "existing">("new");
  const [phone, setPhone] = useState("");
  const [context, setContext] = useState<Context>(eventSlug ? "event" : fixedMobile ? "mobile" : "clinic");
  const [place, setPlace] = useState<Place | null>(null);
  const [category, setCategory] = useState<string | null>(null);
  const [planKind, setPlanKind] = useState<PlanKindOnline>("package");
  const [bookerId, setBookerId] = useState<string | null>(null);
  const [guests, setGuests] = useState<Guest[]>([meGuest()]);
  const [date, setDate] = useState<string | null>(null);
  const [occurrenceId, setOccurrenceId] = useState<string | null>(null);
  const [time, setTime] = useState<string | null>(null);
  const [notes, setNotes] = useState("");
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [done, setDone] = useState<OnlineConfirmation | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // This try at booking, for counting where people stop (nothing personal is sent).
  const [attemptId, setAttemptId] = useState(newAttemptId);

  const data = page.data;
  const event = eventPage.data ?? null;
  const me = data?.me ?? null;
  const setMe = (m: OnlineMe | null) => qc.setQueryData<OnlineClinic>(["book", slug], (cur) => (cur ? { ...cur, me: m } : cur));
  const askWhere = !eventSlug && !fixedMobile && !!data?.mobile;
  const adults = me?.people.filter((p) => !p.minor) ?? [];
  const booker = me?.people.find((p) => p.id === bookerId) ?? null;
  const menu = data ? menuFor(data, context, event) : null;
  const categories = useMemo(
    () => (data && menu && context !== "event" ? data.categories.filter((c) => menu.services.some((s) => s.category === c.name)) : []),
    [data, menu, context],
  );
  // Packages and memberships sit with the categories (IV Drips, Injections, Packages, Memberships).
  const planKinds = context === "event" ? [] : PLAN_GROUPS.filter((g) => data?.plans.some((p) => p.kind === g.kind));
  const chooseKind = categories.length + planKinds.length > 1;

  // Where a step can't be reached from here (a reload, or a link to the middle), start over.
  const ready: Record<Step, boolean> = {
    start: true,
    home: !!me,
    where: askWhere,
    address: context === "mobile" && !!data?.mobile,
    category: true,
    plans: planKinds.length > 0,
    service: true,
    who: !!guests[0]?.services.length,
    time: guests.length > 0 && guests.every((g) => g.services.length),
    phone: true,
    code: !!phone,
    "pick-me": !!me,
    about: !!me,
    review: !!booker && !!date && !!time && guests.every((g) => g.who !== "me" || !!g.clientId),
    done: !!done,
  };
  useMarketingTags(data?.tracking);
  const started = useRef(false);
  useEffect(() => {
    if (step === "time" && !started.current) {
      started.current = true;
      trackStartedBooking();
    }
  }, [step]);
  useEffect(() => {
    if (!data || (eventSlug && !event)) return;
    if (!ready[step]) go(me ? "home" : "start", true);
    // Remembered on this phone: straight to their page.
    else if (step === "start" && me && !preService && !preFor) go("home", true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, data, event, me]);

  useEffect(() => {
    if (!data) return;
    void post(`${base}/attempts`, {
      id: attemptId,
      step,
      context,
      serviceIds: [...new Set(guests.flatMap((g) => g.services.map((sv) => sv.serviceId)))],
      guests: Math.max(1, guests.length),
      device: deviceKind(),
      start: date && time ? `${date}T${time}` : null,
    }).catch(() => undefined);
    // Once per step (and per try).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, attemptId, !!data]);

  // Straight from buying a membership or package (?for=…&service=…), or a link to one service (?service=…).
  useEffect(() => {
    if (!data || !menu || (!preFor && !preService)) return;
    const svc = preService ? menu.services.find((s) => s.id === preService || slugify(s.name) === preService) : null;
    const p = preFor && me ? me.people.find((x) => x.id === preFor) : null;
    setSearch(
      (cur) => {
        const n = new URLSearchParams(cur);
        n.delete("for");
        n.delete("service");
        return n;
      },
      { replace: true },
    );
    if (p) {
      const adult = p.minor ? adults[0] : p;
      if (!adult) return;
      setBookerId(adult.id);
      setGuests([clientGuest(p, svc ? one(svc.id) : [])]);
      go(svc ? "time" : "service", true);
    } else if (svc) {
      setGuests([me && adults.length === 1 ? clientGuest(adults[0]!, one(svc.id)) : meGuest(one(svc.id))]);
      if (me && adults.length === 1) setBookerId(adults[0]!.id);
      go("who", true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, preFor, preService]);

  const onError = (err: unknown) => {
    if (err instanceof ApiError && err.code === "client_signed_out") {
      setMe(null);
      setBookerId(null);
      go("phone");
    }
  };

  if (page.isLoading || eventPage.isLoading) return <Frame><Loading /></Frame>;
  if (!data || !menu) return <Frame><Alert>{page.error instanceof ApiError && page.error.status === 404 ? "We couldn't find that booking page." : errorMessage(page.error)}</Alert></Frame>;
  if (eventSlug && !event) return <Frame clinic={data}><Alert>We couldn't find that event. Check the link, or call us.</Alert></Frame>;
  if (fixedMobile && !data.mobile) return <Frame clinic={data}><Alert>Mobile visits can't be booked online right now. Please call us.</Alert></Frame>;

  /** The first step of picking a visit: where (when there's a choice), what kind, then what. */
  const startBooking = (gs: Guest[]) => {
    setGuests(gs);
    setDate(null);
    setTime(null);
    go(askWhere ? "where" : context === "mobile" ? "address" : chooseKind ? "category" : "service");
  };
  const afterPlace = () => go(chooseKind ? "category" : "service");
  const chooseWhere = (c: Context) => {
    setContext(c);
    const m = menuFor(data, c, event);
    // Keep anything already picked that's on this menu.
    setGuests((gs) => gs.map((g) => ({ ...g, services: g.services.filter((sv) => m.services.some((x) => x.id === sv.serviceId)) })));
    if (c === "mobile") go("address");
    else afterPlace();
  };
  /** Signed in: who's booking. One adult on the number is them; more, they pick; none, they tell us. */
  const resolveBooker = (m: OnlineMe) => {
    const grown = m.people.filter((p) => !p.minor);
    if (grown.length === 1) chooseBooker(grown[0]!);
    else go(grown.length ? "pick-me" : "about");
  };
  const chooseBooker = (p: OnlinePerson) => {
    setBookerId(p.id);
    setGuests((gs) => gs.map((g) => (g.who === "me" ? { ...g, who: "client", clientId: p.id, key: p.id, label: p.firstName } : g)));
    go("review");
  };
  const pickTime = (d: string, occ: string | null, t: string) => {
    setDate(d);
    setOccurrenceId(occ);
    setTime(t);
    setNotice(null);
    if (booker && guests.every((g) => g.who !== "me")) go("review");
    else if (me) resolveBooker(me);
    else go("phone");
  };
  const signOut = () => {
    setMe(null);
    setBookerId(null);
    setGuests((gs) => gs.map((g) => (g.who === "client" && g.clientId === bookerId ? meGuest(g.services) : g)));
    void qc.invalidateQueries({ queryKey: ["book", slug] });
    go("start");
  };

  const title: Record<Step, string> = {
    start: event ? event.name : context === "mobile" && fixedMobile ? "Book a mobile IV" : "Book a visit",
    home: booker || adults[0] ? `Welcome back, ${(booker ?? adults[0])!.firstName}` : "Welcome back",
    where: "Where would you like it?",
    address: "Where should we come?",
    category: "What are you looking for?",
    plans: PLAN_GROUPS.find((g) => g.kind === planKind)!.heading,
    service: category ?? (event ? "Pick a treatment" : "Pick a service"),
    who: "Who's coming?",
    time: "Pick a time",
    phone: flow === "existing" ? "Sign in" : "Last step: your mobile number",
    code: "Enter your code",
    "pick-me": "Which one are you?",
    about: "Tell us about you",
    review: "Review and book",
    done: done?.status === "pending" ? "Almost done" : done?.status === "requested" ? "Request sent" : "You're booked",
  };

  if (event && event.bookingMode === "walk_ups") {
    return (
      <Frame clinic={data} event={event}>
        <h1 className="mb-3 text-2xl font-semibold">{event.name}</h1>
        {event.intro && <p className="mb-4 whitespace-pre-line text-ink-soft">{event.intro}</p>}
        <p className="font-semibold">Walk-ups only: no booking needed. Just come by!</p>
        <EventDates event={event} />
      </Frame>
    );
  }

  const saved = step === "start" ? loadSaved(slug) : null;
  const savedServices = saved?.services.filter((sv) => menuFor(data, saved.context, event).services.some((x) => x.id === sv.serviceId)) ?? [];

  return (
    <Frame clinic={data} event={event}>
      <div className="mb-5">
        {step !== "start" && step !== "done" && !(step === "home" && !!me) && (
          <button onClick={() => window.history.back()} className="mb-1 text-sm font-semibold text-muted hover:text-ink">
            ← Back
          </button>
        )}
        <h1 className="text-2xl font-semibold">{title[step]}</h1>
      </div>

      {step === "start" && (
        <div className="flex flex-col gap-3">
          {event?.intro && <p className="mb-1 whitespace-pre-line text-ink-soft">{event.intro}</p>}
          {event && <EventDates event={event} />}
          {savedServices.length > 0 && (
            <button
              onClick={() => {
                setContext(saved!.context);
                setFlow("new");
                setGuests([meGuest(savedServices)]);
                go(saved!.context === "mobile" ? "address" : "who");
              }}
              className="rounded-[var(--radius-panel)] border-2 border-brand bg-white px-4 py-4 text-left"
            >
              <span className="block text-lg font-semibold">Pick up where you left off</span>
              <span className="text-sm text-ink-soft">{savedServices.map((sv) => menuFor(data, saved!.context, event).services.find((x) => x.id === sv.serviceId)?.name).join(" + ")}</span>
            </button>
          )}
          <ChoiceButton
            title="Existing client"
            detail="Sign in with your mobile number to book faster, book again, and see your visits."
            onClick={() => {
              setFlow("existing");
              go(me ? "home" : "phone");
            }}
          />
          <ChoiceButton
            title="New client"
            detail="First time with us? Pick your treatment and a time; we'll get your details at the end."
            onClick={() => {
              setFlow("new");
              startBooking([guests[0]?.who === "me" ? guests[0] : meGuest()]);
            }}
          />
        </div>
      )}
      {step === "home" && me && (
        <HomeStep
          base={base}
          me={me}
          menu={menuFor(data, "clinic", null)}
          booker={booker ?? (adults.length === 1 ? adults[0]! : null)}
          onBooker={(p) => setBookerId(p.id)}
          onSaved={setMe}
          onBook={(p) => {
            setBookerId(p.id);
            setFlow("existing");
            startBooking([clientGuest(p)]);
          }}
          onAgain={(adult, p, services) => {
            setBookerId(adult.id);
            setContext(eventSlug ? "event" : "clinic");
            setGuests([clientGuest(p, services)]);
            setDate(null);
            setTime(null);
            go("time");
          }}
          onSignOut={signOut}
        />
      )}
      {step === "where" && (
        <div className="flex flex-col gap-3">
          <ChoiceButton title="In the clinic" detail="Relax in our lounge." onClick={() => chooseWhere("clinic")} />
          <ChoiceButton
            title="We come to you"
            detail={`For groups of ${data.mobile?.minGuests ?? 2} or more at a home, office, or venue. Book at least ${Math.round((data.mobile?.leadTimeMinutes ?? 120) / 60)} hours ahead${data.mobile?.requiresConfirmation ? "; a nurse confirms your request" : ""}.`}
            onClick={() => chooseWhere("mobile")}
          />
        </div>
      )}
      {step === "address" && data.mobile && (
        <AddressStep
          base={base}
          me={me}
          mobile={data.mobile}
          place={place}
          onError={onError}
          onDone={(p) => {
            setPlace(p);
            afterPlace();
          }}
        />
      )}
      {step === "category" && (
        <div className="flex flex-col gap-3">
          {categories.map((c) => (
            <ChoiceButton
              key={c.name}
              title={c.name}
              detail={c.description}
              onClick={() => {
                setCategory(c.name);
                go("service");
              }}
            />
          ))}
          {planKinds.map((g) => (
            <ChoiceButton
              key={g.kind}
              title={g.heading}
              detail={g.detail}
              onClick={() => {
                setPlanKind(g.kind);
                go("plans");
              }}
            />
          ))}
        </div>
      )}
      {step === "plans" && <PlanOffers slug={slug} clinic={data} kind={planKind} />}
      {step === "service" && (
        <ServiceStep
          menu={menu}
          category={categories.length > 1 ? category : null}
          value={guests[0]?.services ?? []}
          onChange={(services) => setGuests((gs) => gs.map((g, i) => (i === 0 ? { ...g, services } : g)))}
          onNext={() => {
            save(slug, { at: Date.now(), context, services: guests[0]!.services });
            go("who");
          }}
        />
      )}
      {step === "who" && (
        <WhoStep
          menu={menu}
          me={me}
          booker={booker}
          nurses={context === "clinic" ? data.nurses : null}
          guests={guests}
          setGuests={setGuests}
          maxGuests={event?.maxGuests ?? 8}
          minGuests={context === "mobile" ? (data.mobile?.minGuests ?? 1) : 1}
          onNext={() => {
            setTime(null);
            go("time");
          }}
        />
      )}
      {step === "time" && (
        <>
          {notice && <div className="mb-3"><Alert>{notice}</Alert></div>}
          <TimeStep
            base={base}
            context={context}
            window={context === "mobile" && data.mobile ? data.mobile.window : data.window}
            event={event}
            guests={guests}
            date={date}
            occurrenceId={occurrenceId}
            time={time}
            onError={onError}
            onPick={pickTime}
            onDate={(d, occ) => {
              setDate(d);
              setOccurrenceId(occ);
            }}
          />
        </>
      )}
      {step === "phone" && (
        <PhoneStep
          base={base}
          phone={phone}
          setPhone={setPhone}
          onSent={() => go("code")}
          clinic={data}
          context={context}
          intro={flow === "existing" ? "We'll text you a code to sign in." : "We'll text you a code to confirm it's you. If you've been here before, we'll find your record."}
        />
      )}
      {step === "code" && (
        <CodeStep
          base={base}
          phone={phone}
          remember={!inLobby}
          onVerified={(m) => {
            setMe(m);
            if (flow === "existing" && !date) {
              const grown = m.people.filter((p) => !p.minor);
              if (grown.length === 1) setBookerId(grown[0]!.id);
              go(grown.length ? "home" : "about", true);
            } else resolveBooker(m);
          }}
          onResend={() => go("phone")}
        />
      )}
      {step === "pick-me" && me && (
        <div className="flex flex-col gap-2">
          <DobNeeded base={base} people={me.people} onSaved={setMe} />
          {adults.map((p) => (
            <button
              key={p.id}
              disabled={p.needsDob}
              onClick={() => (date ? chooseBooker(p) : (setBookerId(p.id), go("home", true)))}
              className="flex items-center justify-between rounded-[var(--radius-control)] border border-line bg-white px-4 py-3.5 text-left hover:border-brand disabled:opacity-60"
            >
              <span className="font-semibold">
                {p.firstName} {p.lastName}
              </span>
              <span className="text-sm text-muted">{p.needsDob ? "Add date of birth above" : "That's me →"}</span>
            </button>
          ))}
          <button onClick={() => go("about")} className="rounded-[var(--radius-control)] border border-dashed border-line px-4 py-3.5 text-left font-semibold text-brand hover:border-brand">
            + Someone else on {me.phone}
          </button>
          <SignOut base={base} onDone={signOut} />
        </div>
      )}
      {step === "about" && (
        <>
          {!!me?.people.length && !adults.length && (
            // Only children are on this number: a parent or guardian books for them.
            <div className="mb-4">
              <Alert tone="info">
                {me.people.map((p) => p.firstName).join(" and ")} {me.people.length === 1 ? "is" : "are"} on this number. Clients under 18 need a parent or guardian to book for them. If that's you and you've visited
                us before, use your own number instead. Otherwise tell us about you below.
              </Alert>
            </div>
          )}
          <NewPersonStep
            base={base}
            onError={onError}
            onAdded={(m, name) => {
              setMe(m);
              const p = m.people.find((x) => x.firstName.toLowerCase() === name.first.toLowerCase() && x.lastName.toLowerCase() === name.last.toLowerCase() && !x.minor);
              if (!p) return;
              if (date && time) chooseBooker(p);
              else {
                setBookerId(p.id);
                go("home", true);
              }
            }}
          />
          {!!me?.people.length && <SignOut base={base} onDone={signOut} />}
        </>
      )}
      {step === "review" && booker && date && time && (
        <ReviewStep
          base={base}
          clinic={data}
          menu={menu}
          context={context}
          event={event}
          place={place}
          occurrenceId={occurrenceId}
          bookerId={booker.id}
          attemptId={attemptId}
          guests={guests}
          date={date}
          time={time}
          notes={notes}
          setNotes={setNotes}
          answers={answers}
          setAnswers={setAnswers}
          onError={(err) => {
            onError(err);
            if (err instanceof ApiError && err.code === "time_taken") {
              setNotice("Sorry, that time was just taken. Here are the times still open.");
              go("time", true);
            }
          }}
          onBooked={(c) => {
            setDone(c);
            save(slug, null);
            go("done", true);
            void qc.invalidateQueries({ queryKey: ["book", slug] });
          }}
        />
      )}
      {step === "done" && done && done.status === "pending" && done.payment && <PayStep base={base} c={done} onPaid={(status, depositStatus) => setDone({ ...done, status, depositStatus })} />}
      {step === "done" && done && done.status !== "pending" && (
        <DoneStep
          base={base}
          clinic={data}
          c={done}
          context={context}
          place={place}
          names={new Map(guests.map((g) => [g.label, g.services.map((sv) => menu.services.find((x) => x.id === sv.serviceId)?.name ?? "").filter(Boolean).join(" + ")]))}
          share={new Set(guests.flatMap((g, i) => (g.person && !isMinor(g.person.dob) ? [i] : [])))}
          onAnother={() => {
            setGuests([booker ? clientGuest(booker) : meGuest()]);
            setDate(null);
            setTime(null);
            setNotes("");
            setDone(null);
            setAttemptId(newAttemptId());
            go(me ? "home" : "start");
          }}
        />
      )}
    </Frame>
  );
}

const newAttemptId = () => (typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : "00000000-0000-4000-8000-000000000000".replace(/0/g, () => Math.floor(Math.random() * 16).toString(16)));
const deviceKind = () => (window.matchMedia("(max-width: 640px)").matches ? "phone" : window.matchMedia("(max-width: 1024px)").matches ? "tablet" : "desktop");

const slugify = (s: string) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

function ChoiceButton({ title, detail, onClick }: { title: string; detail?: string; onClick: () => void }) {
  return (
    <button onClick={onClick} className="rounded-[var(--radius-panel)] border border-line bg-white px-4 py-4 text-left hover:border-brand">
      <span className="block text-lg font-semibold">{title}</span>
      {detail && <span className="text-sm text-ink-soft">{detail}</span>}
    </button>
  );
}

/** Signed in: book, book again, and their upcoming visits. (The full client portal comes later.) */
function HomeStep({
  base,
  me,
  menu,
  booker,
  onBooker,
  onSaved,
  onBook,
  onAgain,
  onSignOut,
}: {
  base: string;
  me: OnlineMe;
  menu: Menu;
  booker: OnlinePerson | null;
  onBooker: (p: OnlinePerson) => void;
  onSaved: (m: OnlineMe) => void;
  onBook: (booker: OnlinePerson) => void;
  onAgain: (booker: OnlinePerson, person: OnlinePerson, services: Guest["services"]) => void;
  onSignOut: () => void;
}) {
  const adults = me.people.filter((p) => !p.minor);
  const name = (id: string) => menu.services.find((s) => s.id === id)?.name;
  // Book again: their last visit, if it's all still on the menu.
  const again = booker
    ? me.people
        .filter((p) => !p.needsDob && (p.id === booker.id || p.minor) && p.lastVisit.length && p.lastVisit.every((sv) => name(sv.serviceId)))
        .map((p) => ({ p, services: p.lastVisit.map((sv) => ({ serviceId: sv.serviceId, addOnIds: sv.addOnIds.filter((id) => menu.addOns.some((a) => a.id === id)) })) }))
    : [];
  return (
    <div className="flex flex-col gap-3">
      <DobNeeded base={base} people={me.people} onSaved={onSaved} />
      {adults.length > 1 && (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-ink-soft">Booking as</span>
          {adults.map((p) => (
            <button key={p.id} onClick={() => onBooker(p)} className={cx("rounded-full border px-3 py-1.5 font-semibold", booker?.id === p.id ? "border-brand bg-brand text-white" : "border-line bg-white hover:border-brand")}>
              {p.firstName}
            </button>
          ))}
        </div>
      )}
      {booker && (
        <>
          <ChoiceButton title="Book a visit" detail="For you, your family, or a group." onClick={() => onBook(booker)} />
          {again.map(({ p, services }) => (
            <ChoiceButton
              key={p.id}
              title={p.id === booker.id ? "Book again" : `Book again for ${p.firstName}`}
              detail={services.map((sv) => [name(sv.serviceId), ...sv.addOnIds.map((id) => menu.addOns.find((a) => a.id === id)?.name)].filter(Boolean).join(" + ")).join(" + ")}
              onClick={() => onAgain(booker, p, services)}
            />
          ))}
        </>
      )}
      {!booker && adults.length > 1 && <p className="text-sm text-muted">Pick who you are above to book.</p>}
      {me.upcoming.length > 0 && (
        <section className="mt-3 rounded-[var(--radius-panel)] border border-line bg-white p-4">
          <h2 className="mb-2 font-semibold">Upcoming visits</h2>
          <ul className="flex flex-col gap-2 text-sm">
            {me.upcoming.map((v, i) => (
              <li key={i} className="flex flex-wrap justify-between gap-2">
                <span>
                  <span className="font-semibold">{dateLabel(v.start.slice(0, 10), { weekday: "short", month: "short", day: "numeric" })}</span> {time12(v.start)} · {v.firstName}: {v.serviceName}
                </span>
                {v.status === "unconfirmed" && <span className="text-xs font-semibold text-wait">Reply C to confirm</span>}
              </li>
            ))}
          </ul>
          <p className="mt-2 text-xs text-muted">Need to change one? Call or text us.</p>
        </section>
      )}
      <SignOut base={base} onDone={onSignOut} />
    </div>
  );
}

/** One person's services: each with the add-ons that fit it, and more services that don't break the visit rules. */
function ServicesEditor({ menu, value, onChange, compact = false }: { menu: Menu; value: Guest["services"]; onChange: (v: Guest["services"]) => void; compact?: boolean }) {
  const addOnName = (id: string) => menu.addOns.find((a) => a.id === id)?.name ?? "";
  const addable = menu.services.filter((s) => !value.some((sv) => sv.serviceId === s.id) && !breaksRules(menu, [...value, { serviceId: s.id, addOnIds: [] }]));
  const byCategory = [...new Set(addable.map((s) => s.category || "Services"))].map((cat) => [cat, addable.filter((s) => (s.category || "Services") === cat)] as const);
  return (
    <div className="flex flex-col gap-3">
      {value.map((sv, k) => {
        const svc = menu.services.find((x) => x.id === sv.serviceId);
        if (!svc) return null;
        const setOne = (patch: Partial<Guest["services"][number]>) => onChange(value.map((x, m) => (m === k ? { ...x, ...patch } : x)));
        // Add-ons that would double up a medication already in the visit are left out.
        const addOns = svc.addOnIds.filter((id) => sv.addOnIds.includes(id) || !breaksRules(menu, value.map((x, m) => (m === k ? { ...x, addOnIds: [...x.addOnIds, id] } : x))));
        return (
          <div key={sv.serviceId} className="rounded-[var(--radius-control)] border border-line bg-white p-3">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-semibold">
                  {svc.name} <span className="font-normal text-ink-soft">· {formatCents(svc.priceCents)}</span>
                </div>
                {!compact && svc.description && <p className="mt-1 text-sm text-ink-soft">{svc.description}</p>}
                {!compact && (
                  <p className="mt-1 text-sm text-muted">
                    About {svc.minutes} minutes{svc.includedAddOnIds.length > 0 && ` · Includes ${svc.includedAddOnIds.map(addOnName).filter(Boolean).join(", ")}`}
                  </p>
                )}
              </div>
              {value.length > 1 && (
                <button className="text-sm text-muted hover:text-danger" onClick={() => onChange(value.filter((_, m) => m !== k))} aria-label={`Remove ${svc.name}`}>
                  Remove
                </button>
              )}
            </div>
            {addOns.length > 0 && (
              <details className="mt-2" open={sv.addOnIds.length > 0}>
                <summary className="cursor-pointer text-sm font-semibold text-brand">
                  Add-ons (optional){sv.addOnIds.length > 0 && ` · ${sv.addOnIds.length}`}
                </summary>
                <div className="mt-2 flex flex-col gap-1">
                  {addOns.map((id) => {
                    const a = menu.addOns.find((x) => x.id === id);
                    if (!a) return null;
                    return (
                      <Checkbox
                        key={id}
                        label={`${a.name} · ${formatCents(a.priceCents)}`}
                        description={compact ? undefined : a.description || undefined}
                        checked={sv.addOnIds.includes(id)}
                        onChange={(on) => setOne({ addOnIds: on ? [...sv.addOnIds, id] : sv.addOnIds.filter((x) => x !== id) })}
                      />
                    );
                  })}
                </div>
              </details>
            )}
          </div>
        );
      })}
      {byCategory.length > 0 && (
        <SelectField label={value.length ? "Add another service (optional)" : "Service"} value="" onChange={(e) => e.target.value && onChange([...value, { serviceId: e.target.value, addOnIds: [] }])}>
          <option value="">{value.length ? "Add an injection or another service…" : "Pick a service"}</option>
          {byCategory.map(([cat, list]) => (
            <optgroup key={cat} label={cat}>
              {list.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.name} · {formatCents(x.priceCents)}
                </option>
              ))}
            </optgroup>
          ))}
        </SelectField>
      )}
    </div>
  );
}

/** The bar at the bottom while picking: a rough total and the next button. */
function NextBar({ total, label, disabled, onNext }: { total: number | null; label: string; disabled?: boolean; onNext: () => void }) {
  return (
    <div className="sticky bottom-0 -mx-4 mt-2 flex items-center gap-3 border-t border-line bg-paper/95 px-4 py-3 backdrop-blur">
      {total != null && (
        <div className="text-sm">
          <span className="text-muted">From </span>
          <span className="font-semibold tabular-nums">{formatCents(total)}</span>
        </div>
      )}
      <Button variant="primary" className="ml-auto flex-1 py-3 sm:flex-none" disabled={disabled} onClick={onNext}>
        {label}
      </Button>
    </div>
  );
}

function ServiceStep({ menu, category, value, onChange, onNext }: { menu: Menu; category: string | null; value: Guest["services"]; onChange: (v: Guest["services"]) => void; onNext: () => void }) {
  if (!menu.services.length) return <Alert tone="info">Nothing can be booked online here yet. Please call us.</Alert>;
  const list = menu.services.filter((s) => !category || s.category === category);
  if (!value.length) {
    return (
      <div className="flex flex-col gap-2">
        {list.map((s) => (
          <button key={s.id} onClick={() => onChange(one(s.id))} className="rounded-[var(--radius-panel)] border border-line bg-white px-4 py-3.5 text-left hover:border-brand">
            <span className="flex items-baseline justify-between gap-3">
              <span className="font-semibold">{s.name}</span>
              <span className="shrink-0 tabular-nums text-ink-soft">{formatCents(s.priceCents)}</span>
            </span>
            {s.description && <span className="mt-1 block text-sm text-ink-soft">{s.description}</span>}
            <span className="mt-1 block text-xs text-muted">About {s.minutes} minutes</span>
          </button>
        ))}
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-3">
      <ServicesEditor menu={menu} value={value} onChange={onChange} />
      <button onClick={() => onChange([])} className="self-start text-sm font-semibold text-brand">
        Choose a different service
      </button>
      {menu.groupDiscount && <p className="text-sm text-muted">Groups: {menu.groupDiscount}.</p>}
      <NextBar total={estimate(menu, [{ ...meGuest(), services: value }])} label="Continue" onNext={onNext} />
    </div>
  );
}

const emptyPerson = (): Person & { email: string; mobile: string } => ({ firstName: "", lastName: "", dob: "", email: "", mobile: "", guardian: false, saveAsFamily: true });

/** A guest typed in: name and birthday; adults also a mobile and email (for their own texts); children the guardian tick. */
function PersonForm({ child = false, submit, onAdd, onCancel }: { child?: boolean; submit: string; onAdd: (p: Person) => void; onCancel?: () => void }) {
  const [p, setP] = useState(emptyPerson());
  const minor = isMinor(p.dob);
  const wrongAge = child && !!p.dob && !minor;
  return (
    <form
      className="grid gap-3 sm:grid-cols-2"
      onSubmit={(e) => {
        e.preventDefault();
        if ((minor && !p.guardian) || wrongAge) return;
        onAdd({ ...p, firstName: p.firstName.trim(), lastName: p.lastName.trim(), email: p.email.trim() || null, mobile: p.mobile.trim() || null });
        setP(emptyPerson());
      }}
    >
      {/* No autofill: the booker's own details mustn't land on a guest. */}
      <Field label="First name" required autoComplete="off" value={p.firstName} onChange={(e) => setP({ ...p, firstName: e.target.value })} />
      <Field label="Last name" required autoComplete="off" value={p.lastName} onChange={(e) => setP({ ...p, lastName: e.target.value })} />
      <DateOfBirthField required max={todayIso()} value={p.dob} onChange={(d) => setP({ ...p, dob: d })} error={wrongAge ? "They're 18 or older: choose \"Me + guests\" to book for another adult." : undefined} />
      {!child && (
        <>
          <Field label="Mobile" type="tel" autoComplete="off" required={!minor} hint={minor ? "Optional for children." : "We'll text them their forms."} value={p.mobile} onChange={(e) => setP({ ...p, mobile: e.target.value })} />
          <Field label="Email" type="email" autoComplete="off" required={!minor} hint={minor ? "Optional for children." : undefined} value={p.email} onChange={(e) => setP({ ...p, email: e.target.value })} />
        </>
      )}
      {!child && !!p.dob && !minor && (
        <div className="sm:col-span-2">
          <Checkbox label={`Save ${p.firstName.trim() || "them"} as family, so they're one tap next time`} checked={p.saveAsFamily} onChange={(on) => setP({ ...p, saveAsFamily: on })} />
        </div>
      )}
      {minor && (
        <div className="sm:col-span-2">
          <Checkbox label={GUARDIAN_WORDING} checked={p.guardian} onChange={(on) => setP({ ...p, guardian: on })} />
          <p className="px-2 text-xs text-muted">Their forms and messages come to you.</p>
        </div>
      )}
      <div className="flex items-end gap-2 sm:col-span-2">
        <Button type="submit" disabled={(minor && !p.guardian) || wrongAge}>
          {submit}
        </Button>
        {onCancel && (
          <Button type="button" variant="quiet" onClick={onCancel}>
            Cancel
          </Button>
        )}
      </div>
    </form>
  );
}

function WhoStep({
  menu,
  me,
  booker,
  nurses,
  guests,
  setGuests,
  maxGuests,
  minGuests,
  onNext,
}: {
  menu: Menu;
  me: OnlineMe | null;
  booker: OnlinePerson | null;
  nurses: { id: string; name: string }[] | null;
  guests: Guest[];
  setGuests: (g: Guest[]) => void;
  maxGuests: number;
  /** Mobile visits: at least this many guests. */
  minGuests: number;
  onNext: () => void;
}) {
  const first = guests[0]!;
  const isMe = (g: Guest) => g.who === "me" || (!!booker && g.clientId === booker.id);
  const [group, setGroup] = useState(guests.length > 1);
  const mode: "me" | "child" | "group" = !isMe(first) ? "child" : guests.length > 1 || group ? "group" : "me";
  const [adding, setAdding] = useState(false);
  const meAgain = (): Guest => (booker ? clientGuest(booker, first.services) : meGuest(first.services));
  // On this number, then linked family (adults only: children are booked by their parent or guardian).
  const known = [...(me?.people.filter((p) => !p.needsDob) ?? []), ...(me?.family ?? [])];
  const children = me?.people.filter((p) => p.minor && !p.needsDob) ?? [];
  const others = known.filter((p) => p.id !== booker?.id && !guests.some((g) => g.clientId === p.id));
  // Family show their last initial, in case a first name is shared.
  const chip = (p: (typeof known)[number]) => (me?.family.some((f) => f.id === p.id) ? `${p.firstName} ${p.lastName}` : p.firstName);
  const set = (i: number, patch: Partial<Guest>) => setGuests(guests.map((g, j) => (j === i ? { ...g, ...patch } : g)));
  const newGuest = (p: Person, services: Guest["services"]): Guest => ({ key: `${Date.now()}`, who: "new", clientId: null, person: p, label: p.firstName, services, staffId: null });
  const childChosen = mode === "child" && (first.who === "client" || !!first.person);

  // What's being booked, said plainly (it can be picked before this step, e.g. from the lobby screen or a link).
  const picked = first.services.map((x) => menu.services.find((m) => m.id === x.serviceId)).filter((x): x is Menu["services"][number] => !!x);
  return (
    <div className="flex flex-col gap-4">
      {picked.length > 0 && (
        <div className="rounded-[10px] border border-line bg-white px-4 py-3">
          <div className="text-xs font-semibold uppercase tracking-wide text-muted">Booking</div>
          {picked.map((x) => (
            <div key={x.id} className="mt-1">
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-lg font-semibold">{x.name}</span>
                <span className="whitespace-nowrap text-sm text-ink-soft">
                  {x.minutes} min · {formatCents(x.priceCents)}
                </span>
              </div>
              {x.description && <p className="text-sm text-ink-soft">{x.description}</p>}
            </div>
          ))}
        </div>
      )}
      <div className="grid gap-2 sm:grid-cols-3" role="radiogroup" aria-label="Who's coming">
        {(
          [
            ["me", "Just me"],
            ["child", "Just my child"],
            ["group", "Me + guests"],
          ] as const
        ).map(([m, label]) => (
          <button
            key={m}
            role="radio"
            aria-checked={mode === m}
            onClick={() => {
              setGroup(m === "group");
              setAdding(m === "group" && mode !== "group");
              if (m === "me") setGuests([meAgain()]);
              else if (m === "child") setGuests([{ ...meGuest(first.services), who: "new", key: "child", label: "Your child" }]);
              else if (mode !== "group") setGuests([meAgain()]);
            }}
            className={cx("rounded-[var(--radius-control)] border px-3 py-3 text-left font-semibold", mode === m ? "border-brand bg-brand-wash text-brand" : "border-line bg-white hover:border-brand")}
          >
            {label}
          </button>
        ))}
      </div>

      {mode === "child" && (
        <section className="rounded-[var(--radius-panel)] border border-line bg-white p-4">
          {childChosen ? (
            <div className="flex items-center justify-between">
              <span className="font-semibold">{first.person ? `${first.person.firstName} ${first.person.lastName}` : first.label}</span>
              <button className="text-sm font-semibold text-brand" onClick={() => set(0, { who: "new", clientId: null, person: null, key: "child", label: "Your child" })}>
                Change
              </button>
            </div>
          ) : (
            <>
              {children.length > 0 && (
                <div className="mb-3 flex flex-wrap gap-2">
                  {children.map((p) => (
                    <button key={p.id} className="rounded-full border border-line px-3 py-1.5 text-sm font-semibold hover:border-brand" onClick={() => setGuests([clientGuest(p, first.services)])}>
                      {chip(p)}
                    </button>
                  ))}
                </div>
              )}
              <h2 className="mb-2 font-semibold">{children.length ? "Or another child" : "Your child"}</h2>
              <PersonForm child submit="Use this child" onAdd={(p) => setGuests([newGuest(p, first.services)])} />
            </>
          )}
        </section>
      )}

      {mode === "group" && (
        <div className="flex flex-col gap-3">
          {guests.map((g, i) => (
            <section key={g.key} className="rounded-[var(--radius-panel)] border border-line bg-white p-4">
              <div className="mb-2 flex items-center justify-between">
                <h2 className="font-semibold">{isMe(g) ? (booker ? `${booker.firstName} (you)` : "You") : g.label}</h2>
                {i > 0 && (
                  <button className="text-sm text-muted hover:text-danger" onClick={() => setGuests(guests.filter((_, j) => j !== i))}>
                    Remove
                  </button>
                )}
              </div>
              {i === 0 ? (
                <p className="text-sm text-ink-soft">{g.services.map((sv) => menu.services.find((x) => x.id === sv.serviceId)?.name).join(" + ")}</p>
              ) : (
                <>
                  {!g.services.length && <p className="mb-2 text-sm font-semibold text-brand">Pick a service for {g.label}.</p>}
                  <ServicesEditor compact menu={menu} value={g.services} onChange={(services) => set(i, { services })} />
                </>
              )}
            </section>
          ))}
          {adding ? (
            <section className="rounded-[var(--radius-panel)] border border-line bg-white p-4">
              <h2 className="mb-3 font-semibold">Add a guest</h2>
              {others.length > 0 && (
                <div className="mb-3 flex flex-wrap gap-2">
                  {others.map((p) => (
                    <button
                      key={p.id}
                      className="rounded-full border border-line px-3 py-1.5 text-sm font-semibold hover:border-brand"
                      onClick={() => {
                        setGuests([...guests, clientGuest(p, [])]);
                        setAdding(false);
                      }}
                    >
                      + {chip(p)}
                    </button>
                  ))}
                </div>
              )}
              <PersonForm
                submit="Add guest"
                onAdd={(p) => {
                  setGuests([...guests, newGuest(p, [])]);
                  setAdding(false);
                }}
                onCancel={() => setAdding(false)}
              />
              <p className="mt-2 text-xs text-muted">You'll pick their service next. Adults get a text with a link to fill in their own forms.</p>
            </section>
          ) : (
            guests.length < maxGuests && (
              <button onClick={() => setAdding(true)} className="rounded-[var(--radius-control)] border border-dashed border-line px-4 py-3 text-left font-semibold text-brand hover:border-brand">
                + Add a guest
              </button>
            )
          )}
          {menu.groupDiscount && <p className="text-sm text-muted">Groups: {menu.groupDiscount}.</p>}
        </div>
      )}

      {nurses && (
        <SelectField label={guests.length > 1 ? "Nurse (for everyone)" : "Nurse"} value={first.staffId ?? ""} onChange={(e) => setGuests(guests.map((g) => ({ ...g, staffId: e.target.value || null })))}>
          <option value="">Any nurse</option>
          {nurses.map((n) => (
            <option key={n.id} value={n.id}>
              {n.name}
            </option>
          ))}
        </SelectField>
      )}
      {guests.length < minGuests && (
        <Alert tone="info">Mobile visits are for groups of {minGuests} or more. Add {minGuests - guests.length === 1 ? "another guest" : `${minGuests - guests.length} more guests`}, or book a visit at the clinic.</Alert>
      )}
      <NextBar
        total={estimate(menu, guests)}
        label="Pick a time"
        disabled={guests.length < minGuests || guests.some((g) => !g.services.length) || (mode === "child" && !childChosen) || adding}
        onNext={onNext}
      />
    </div>
  );
}

function EventDates({ event }: { event: OnlineEvent }) {
  if (!event.occurrences.length) return <p className="my-4 text-ink-soft">No upcoming dates yet. Check back soon.</p>;
  return (
    <ul className="my-4 flex flex-col gap-1 text-sm">
      {event.occurrences.slice(0, 6).map((o) => (
        <li key={o.id}>
          <span className="font-semibold">{dateLabel(o.start.slice(0, 10), { weekday: "long", month: "long", day: "numeric" })}</span>, {time12(o.start)}–{time12(o.end)}
        </li>
      ))}
      {event.venue && <li className="mt-1 text-muted">{event.venue}</li>}
    </ul>
  );
}

export function Frame({ clinic, event, children }: { clinic?: OnlineClinic; event?: OnlineEvent | null; children: ReactNode }) {
  const accent = event?.accentColor ?? clinic?.clinic.accentColor;
  const style = accent ? ({ "--color-brand": accent, "--color-brand-deep": accent } as CSSProperties) : undefined;
  useEffect(() => {
    if (clinic) document.title = `Book · ${event?.name ?? clinic.clinic.name}`;
    // Private event links stay out of search engines.
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex";
    if (event?.unlisted) document.head.appendChild(meta);
    return () => meta.remove();
  }, [clinic, event]);
  const logo = event?.logoUrl ?? clinic?.clinic.logoUrl;
  return (
    <div className="min-h-dvh bg-paper" style={style}>
      <header className="px-4 py-4 text-white" style={{ background: event?.primaryColor ?? clinic?.clinic.primaryColor ?? "var(--color-ink)" }}>
        <div className="mx-auto flex max-w-lg items-center justify-between gap-3">
          {logo ? <img src={logo} alt={event?.host || clinic?.clinic.name} className="h-9 w-auto" /> : <span className="font-display text-xl">{event?.host || clinic?.clinic.name}</span>}
          {event && <span className="text-sm opacity-80">with {clinic?.clinic.name}</span>}
        </div>
      </header>
      <main className="mx-auto max-w-lg px-4 py-6">{children}</main>
      {clinic?.clinic.phone && (
        <footer className="mx-auto max-w-lg px-4 pb-8 text-sm text-muted">
          Questions? Call{" "}
          <a className="font-semibold underline" href={`tel:${clinic.clinic.phone}`}>
            {clinic.clinic.phone}
          </a>
          .
        </footer>
      )}
    </div>
  );
}

export function PhoneStep({
  base,
  phone,
  setPhone,
  onSent,
  clinic,
  context,
  intro,
}: {
  base: string;
  phone: string;
  setPhone: (v: string) => void;
  onSent: () => void;
  clinic: OnlineClinic;
  context: Context;
  /** Replaces the booking wording (e.g. on a membership sign-up page). */
  intro?: string;
}) {
  const send = useMutation({ mutationFn: () => post(`${base}/code`, { phone }), onSuccess: onSent });
  return (
    <form className="flex flex-col gap-4" onSubmit={(e) => (e.preventDefault(), send.mutate())}>
      {intro ? (
        <p className="text-ink-soft">{intro}</p>
      ) : context !== "event" && (
        <p className="text-ink-soft">
          {context === "mobile" ? `${clinic.clinic.name} comes to you.` : `Book a visit at ${clinic.clinic.name}.`} We'll text you a code to sign in. You can book for yourself and anyone coming with you.
        </p>
      )}
      <Field label="Mobile number" type="tel" inputMode="tel" autoComplete="tel" required autoFocus value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(405) 555-0101" />
      {send.error && <Alert>{errorMessage(send.error)}</Alert>}
      <Button variant="primary" type="submit" busy={send.isPending} className="py-3">
        Text me a code
      </Button>
      <p className="text-xs text-muted">Message and data rates may apply.</p>
    </form>
  );
}

export function CodeStep({ base, phone, onVerified, onResend, remember: offerRemember = false }: { base: string; phone: string; onVerified: (m: OnlineMe) => void; onResend: () => void; remember?: boolean }) {
  const [code, setCode] = useState("");
  const [remember, setRemember] = useState(false);
  const verify = useMutation({ mutationFn: () => post<OnlineMe>(`${base}/verify`, { phone, code, remember }), onSuccess: onVerified });
  return (
    <form className="flex flex-col gap-4" onSubmit={(e) => (e.preventDefault(), verify.mutate())}>
      <p className="text-ink-soft">We texted a 6-digit code to {formatPhone(phone) || phone}.</p>
      <Field label="Code" inputMode="numeric" autoComplete="one-time-code" required autoFocus maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} />
      {offerRemember && <Checkbox label="Remember this phone" description="Stay signed in for booking for 30 days. Only on a phone that's yours." checked={remember} onChange={setRemember} />}
      {verify.error && <Alert>{errorMessage(verify.error)}</Alert>}
      <Button variant="primary" type="submit" busy={verify.isPending} disabled={code.length !== 6} className="py-3">
        Continue
      </Button>
      <button type="button" onClick={onResend} className="text-sm font-semibold text-brand">
        Didn't get it? Send a new code
      </button>
    </form>
  );
}

/** People on the account with no date of birth on file (imported from the old system) add it before booking. */
export function DobNeeded({ base, people, onSaved }: { base: string; people: OnlinePerson[]; onSaved: (me: OnlineMe) => void }) {
  const missing = people.filter((p) => p.needsDob);
  const [dobs, setDobs] = useState<Record<string, string>>({});
  const save = useMutation({
    mutationFn: (id: string) => post<OnlineMe>(`${base}/people/${id}/dob`, { dob: dobs[id] }),
    onSuccess: onSaved,
  });
  if (!missing.length) return null;
  return (
    <div className="mb-2 flex flex-col gap-3 rounded-[var(--radius-control)] border border-wait/40 bg-wait-wash p-4">
      <p className="text-sm font-semibold">We need a date of birth before booking.</p>
      {missing.map((p) => (
        <form
          key={p.id}
          className="flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate(p.id);
          }}
        >
          <DateOfBirthField label={`${p.firstName} ${p.lastName}`} required max={todayIso()} value={dobs[p.id] ?? ""} onChange={(d) => setDobs({ ...dobs, [p.id]: d })} />
          <Button type="submit" busy={save.isPending && save.variables === p.id} disabled={!dobs[p.id]}>
            Save
          </Button>
        </form>
      ))}
      {save.error && <Alert>{errorMessage(save.error)}</Alert>}
    </div>
  );
}

export function SignOut({ base, onDone }: { base: string; onDone: () => void }) {
  const out = useMutation({ mutationFn: () => post(`${base}/signout`), onSuccess: onDone });
  return (
    <button onClick={() => out.mutate()} className="mt-4 self-start text-sm text-muted underline">
      Not you? Use a different number
    </button>
  );
}

export function NewPersonStep({ base, onAdded, onError }: { base: string; onAdded: (m: OnlineMe, name: { first: string; last: string }) => void; onError: (e: unknown) => void }) {
  const [v, setV] = useState({ firstName: "", lastName: "", dob: "", email: "" });
  const [consents, setConsents] = useState<Record<string, boolean>>({ sms_reminders: true, email_reminders: true, sms_marketing: false, email_marketing: false });
  const add = useMutation({
    mutationFn: () => post<OnlineMe>(`${base}/people`, { ...v, consents }),
    onSuccess: (m) => onAdded(m, { first: v.firstName.trim(), last: v.lastName.trim() }),
    onError,
  });
  const minor = v.dob && ageOn(v.dob, todayIso()) < 18;
  return (
    <form className="flex flex-col gap-4" onSubmit={(e) => (e.preventDefault(), add.mutate())}>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="First name" required autoComplete="given-name" value={v.firstName} onChange={(e) => setV({ ...v, firstName: e.target.value })} />
        <Field label="Last name" required autoComplete="family-name" value={v.lastName} onChange={(e) => setV({ ...v, lastName: e.target.value })} />
      </div>
      <DateOfBirthField required max={todayIso()} value={v.dob} onChange={(d) => setV({ ...v, dob: d })} error={minor ? "Under 18? A parent or guardian signs in with their own number and adds you as a guest." : undefined} />
      <Field label="Email" type="email" required autoComplete="email" value={v.email} onChange={(e) => setV({ ...v, email: e.target.value })} />
      <fieldset className="flex flex-col gap-2.5">
        <legend className="mb-1 text-sm font-semibold text-ink-soft">Messages from us</legend>
        {CONSENT_CHANNELS.map((c) => (
          <Checkbox key={c} label={ONLINE_CONSENT_WORDING[c]} checked={!!consents[c]} onChange={(on) => setConsents({ ...consents, [c]: on })} />
        ))}
      </fieldset>
      {!!add.error && <Alert>{errorMessage(add.error)}</Alert>}
      <Button variant="primary" type="submit" busy={add.isPending} disabled={!!minor} className="py-3">
        Continue
      </Button>
    </form>
  );
}

function AddressStep({
  base,
  me,
  mobile,
  place,
  onDone,
  onError,
}: {
  base: string;
  me: OnlineMe | null;
  mobile: NonNullable<OnlineClinic["mobile"]>;
  place: Place | null;
  onDone: (p: Place) => void;
  onError: (e: unknown) => void;
}) {
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [found, setFound] = useState<{ address: ResolvedAddress; check: AddressCheck } | null>(place ? { address: { ...place.address, label: place.label }, check: place.check } : null);
  const [line2, setLine2] = useState(place?.address.line2 ?? "");
  const [type, setType] = useState(place?.locationType ?? "");
  const [ack, setAck] = useState(!!place);
  const [noticeOk, setNoticeOk] = useState(!!place?.noticeAgreed);
  const [outsideOk, setOutsideOk] = useState(!!place?.outsideAgreed);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query]);
  const suggestions = useQuery({
    queryKey: ["book-address", base, debounced],
    queryFn: () => post<{ id: string; label: string }[]>(`${base}/address/suggest`, { q: debounced }),
    enabled: debounced.length >= 4 && !found,
  });
  const check = useMutation({
    mutationFn: (v: { id?: string; text?: string }) => post<{ address: ResolvedAddress; check: AddressCheck }>(`${base}/address/check`, v),
    onSuccess: (r) => {
      setFound(r);
      setLine2(r.address.line2);
    },
    onError,
  });

  const notice = mobile.locationTypes.find((t) => t.key === type)?.notice ?? "";

  if (!found) {
    return (
      <div className="flex flex-col gap-3">
        {!!me?.addresses.length && (
          <div className="flex flex-col gap-2">
            {me.addresses.map((a) => (
              <button key={a.text} onClick={() => check.mutate({ text: a.text })} className="rounded-[var(--radius-control)] border border-line bg-white px-4 py-3 text-left hover:border-brand">
                <span className="block text-sm font-semibold">{a.label}</span>
                <span className="text-sm text-ink-soft">{a.text}</span>
              </button>
            ))}
            <p className="mt-1 text-sm text-muted">Or a different address:</p>
          </div>
        )}
        <Field label="Address" autoComplete="street-address" autoFocus placeholder="Start typing the street address" value={query} onChange={(e) => setQuery(e.target.value)} />
        {suggestions.data && suggestions.data.length > 0 && (
          <ul className="overflow-hidden rounded-[var(--radius-control)] border border-line bg-white">
            {suggestions.data.map((s) => (
              <li key={s.id}>
                <button onClick={() => check.mutate({ id: s.id })} className="w-full border-b border-mist px-4 py-2.5 text-left text-sm last:border-0 hover:bg-mist">
                  {s.label}
                </button>
              </li>
            ))}
          </ul>
        )}
        {debounced.length >= 4 && suggestions.data?.length === 0 && <p className="text-sm text-muted">No matches yet. Keep typing the full address.</p>}
        {check.isPending && <Loading />}
        {!!check.error && <Alert>{errorMessage(check.error)}</Alert>}
      </div>
    );
  }

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        const a = found.address;
        onDone({
          address: { line1: a.line1, line2, city: a.city, state: a.state, postalCode: a.postalCode, latitude: a.latitude, longitude: a.longitude },
          label: a.label,
          check: found.check,
          locationType: type,
          noticeAgreed: !!notice && noticeOk,
          outsideAgreed: found.check.outside && outsideOk,
        });
      }}
    >
      <div className="rounded-[var(--radius-control)] border border-line bg-white px-4 py-3">
        <p className="font-semibold">{found.address.label}</p>
        <button type="button" className="mt-1 text-sm font-semibold text-brand" onClick={() => (setFound(null), setAck(false), setOutsideOk(false))}>
          Change
        </button>
      </div>
      {!found.check.ok ? (
        <Alert>{found.check.message}</Alert>
      ) : (
        <>
          {found.check.outside ? (
            <div className="flex flex-col gap-2 rounded-[var(--radius-control)] border border-wait/40 bg-wait-wash px-4 py-3">
              <p className="text-sm font-semibold text-wait">{found.check.message}</p>
              <Checkbox label="I understand this request may be declined." checked={outsideOk} onChange={setOutsideOk} />
            </div>
          ) : (
            <p className="text-sm text-ink-soft">In our coverage area{found.check.travelFeeCents > 0 ? ` · travel fee ${formatCents(found.check.travelFeeCents)}` : " · no travel fee"}.</p>
          )}
          <Field label="Apartment, suite, or room (optional)" value={line2} onChange={(e) => setLine2(e.target.value)} />
          <fieldset className="flex flex-col gap-2">
            <legend className="mb-1 text-sm font-semibold text-ink-soft">What kind of place is it?</legend>
            {mobile.locationTypes.map((t) => (
              <label key={t.key} className={cx("flex cursor-pointer items-center gap-3 rounded-[var(--radius-control)] border bg-white px-4 py-3", type === t.key ? "border-brand" : "border-line")}>
                <input type="radio" name="locationType" checked={type === t.key} onChange={() => (setType(t.key), setNoticeOk(false))} className="accent-[var(--color-brand)]" />
                <span>{t.label}</span>
              </label>
            ))}
          </fieldset>
          {notice && (
            <div className="flex flex-col gap-2 rounded-[var(--radius-control)] border border-line bg-mist px-4 py-3">
              <p className="text-sm">{notice}</p>
              <Checkbox label="I understand." checked={noticeOk} onChange={setNoticeOk} />
            </div>
          )}
          <Checkbox label={mobile.acknowledgment} checked={ack} onChange={setAck} />
          <Button variant="primary" type="submit" className="py-3" disabled={!type || !ack || (!!notice && !noticeOk) || (found.check.outside && !outsideOk)}>
            Continue
          </Button>
        </>
      )}
    </form>
  );
}

const guestBody = (guests: Guest[]) => guests.map((g) => ({ services: g.services, staffId: g.staffId, clientId: g.clientId }));

function TimeStep({
  base,
  context,
  window,
  event,
  guests,
  date,
  occurrenceId,
  time,
  onPick,
  onDate,
  onError,
}: {
  base: string;
  context: Context;
  window: { first: string; last: string };
  event: OnlineEvent | null;
  guests: Guest[];
  date: string | null;
  occurrenceId: string | null;
  time: string | null;
  onPick: (date: string, occurrenceId: string | null, time: string) => void;
  onDate: (date: string, occurrenceId: string | null) => void;
  onError: (e: unknown) => void;
}) {
  // Clinic and mobile: a strip of days. Events: the event's dates.
  const days = useMemo(() => {
    if (event) return event.occurrences.map((o) => ({ date: o.start.slice(0, 10), occurrenceId: o.id as string | null, label: `${time12(o.start)}–${time12(o.end)}` }));
    const out: { date: string; occurrenceId: string | null; label: string }[] = [];
    for (let d = window.first; d <= window.last && out.length < 62; d = addDays(d, 1)) out.push({ date: d, occurrenceId: null, label: "" });
    return out;
  }, [window, event]);
  const current = days.find((d) => (event ? d.occurrenceId === occurrenceId : d.date === date)) ?? days[0];
  const body = current ? { date: current.date, context, occurrenceId: current.occurrenceId, guests: guestBody(guests) } : null;
  const slots = useQuery({
    queryKey: ["book-slots", base, JSON.stringify(body)],
    enabled: !!body,
    queryFn: async () => {
      try {
        return await post<OnlineSlots>(`${base}/slots`, body);
      } catch (err) {
        onError(err);
        throw err;
      }
    },
  });

  // Clinic and mobile: public events on these days, offered when a day has no times (or as an extra).
  const eventDays = useQuery({ queryKey: ["book-event-days", base], queryFn: () => get<OnlineEventDay[]>(`${base}/event-days`), enabled: !event, staleTime: 5 * 60_000 });
  const eventsOn = (d: string) => (event ? [] : (eventDays.data ?? []).filter((x) => x.date === d));

  if (!current) return <p className="text-ink-soft">No dates are open for booking yet.</p>;
  const todaysEvents = eventsOn(current.date);

  return (
    <div className="flex flex-col gap-4">
      <div className="-mx-4 flex snap-x gap-2 overflow-x-auto px-4 pb-1" role="listbox" aria-label="Date">
        {days.map((d) => {
          const on = d === current;
          return (
            <button
              key={d.occurrenceId ?? d.date}
              role="option"
              aria-selected={on}
              onClick={() => onDate(d.date, d.occurrenceId)}
              className={cx("flex shrink-0 snap-start flex-col items-center rounded-[var(--radius-control)] border px-2 py-2", event ? "w-28" : "w-16", on ? "border-brand bg-brand text-white" : "border-line bg-white hover:border-brand")}
            >
              <span className="text-xs">{dateLabel(d.date, { weekday: "short" })}</span>
              <span className="text-lg font-semibold">{dateLabel(d.date, { day: "numeric" })}</span>
              <span className="text-xs">{dateLabel(d.date, { month: "short" })}</span>
              {d.label && <span className="mt-0.5 text-[11px]">{d.label}</span>}
              {eventsOn(d.date).length > 0 && <span className={cx("mt-0.5 text-[10px] font-semibold uppercase", on ? "text-white" : "text-brand")}>Event</span>}
            </button>
          );
        })}
      </div>
      <h2 className="font-semibold">{dateLabel(current.date, { weekday: "long", month: "long", day: "numeric" })}</h2>
      {slots.isLoading ? (
        <Loading />
      ) : slots.error ? (
        <Alert>{errorMessage(slots.error)}</Alert>
      ) : slots.data?.times.length ? (
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
          {slots.data.times.map((t) => {
            // Groups that can't all start at once: each next guest starts when a nurse is free.
            const later = (slots.data.starts[t] ?? []).slice(1).filter((x) => x !== t);
            return (
              <button
                key={t}
                onClick={() => onPick(current.date, current.occurrenceId, t)}
                className={cx("rounded-[var(--radius-control)] border py-2.5 text-sm font-semibold tabular-nums", t === time && current.date === date ? "border-brand bg-brand text-white" : "border-line bg-white hover:border-brand")}
              >
                {time12(`${current.date}T${t}`)}
                {later.length > 0 && <span className="block text-[11px] font-normal">then {[...new Set(later)].map((x) => time12(`${current.date}T${x}`)).join(", ")}</span>}
              </button>
            );
          })}
        </div>
      ) : todaysEvents.length ? (
        <div className="flex flex-col gap-2">
          {todaysEvents.map((x) => (
            <div key={x.url + x.start} className="rounded-[var(--radius-panel)] border border-line bg-white px-4 py-3">
              <p className="text-ink-soft">
                No {context === "mobile" ? "mobile" : "in-clinic"} times on {dateLabel(current.date, { weekday: "long", month: "short", day: "numeric" })}, but we're at <span className="font-semibold text-ink">{x.name}</span>, {time12(x.start)}–{time12(x.end)}.
              </p>
              <EventPlace venue={x.venue} address={x.address} />
              <a href={x.url} className="mt-2 inline-block rounded-[var(--radius-control)] bg-brand px-4 py-2 text-sm font-semibold text-white">
                {x.walkUpsOnly ? `Walk-ups welcome: see ${x.name} →` : `Book at ${x.name} →`}
              </a>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-ink-soft">
          No open times {event ? "left on this date" : "this day"}
          {guests.length > 1 ? " for your group" : ""}. {event?.bookingMode === "both" ? "Walk-ups are welcome too." : "Try another day."}
        </p>
      )}
      {!!slots.data?.times.length &&
        todaysEvents.map((x) => (
          <div key={x.url + x.start} className="text-sm">
            <a href={x.url} className="font-semibold text-brand">
              Also at {x.name} today, {time12(x.start)}–{time12(x.end)} →
            </a>
            <EventPlace venue={x.venue} address={x.address} />
          </div>
        ))}
      {guests.length > 1 && <p className="text-sm text-muted">Times are when the first guest starts. With one nurse free, the next guest starts as soon as they are.</p>}
    </div>
  );
}

/** Where an event is (not the clinic): the venue and its address, which opens in maps. */
function EventPlace({ venue, address }: { venue: string; address: string }) {
  if (!venue && !address) return null;
  return (
    <p className="mt-1 text-sm text-ink-soft">
      {venue && <span className="font-semibold text-ink">{venue}</span>}
      {venue && address && " · "}
      {address && (
        <a className="underline" href={`https://maps.google.com/?q=${encodeURIComponent([venue, address].filter(Boolean).join(", "))}`} target="_blank" rel="noreferrer">
          {address}
        </a>
      )}
    </p>
  );
}

function ReviewStep({
  base,
  clinic,
  menu,
  context,
  event,
  place,
  occurrenceId,
  bookerId,
  attemptId,
  guests,
  date,
  time,
  notes,
  setNotes,
  answers,
  setAnswers,
  onBooked,
  onError,
}: {
  base: string;
  clinic: OnlineClinic;
  menu: Menu;
  context: Context;
  event: OnlineEvent | null;
  place: Place | null;
  occurrenceId: string | null;
  bookerId: string;
  attemptId: string;
  guests: Guest[];
  date: string;
  time: string;
  notes: string;
  setNotes: (v: string) => void;
  answers: Record<string, string>;
  setAnswers: (v: Record<string, string>) => void;
  onBooked: (c: OnlineConfirmation) => void;
  onError: (e: unknown) => void;
}) {
  const body = { date, context, occurrenceId, guests: guestBody(guests) };
  const quote = useQuery({ queryKey: ["book-slots", base, JSON.stringify(body)], queryFn: () => post<OnlineSlots>(`${base}/slots`, body) });
  const [agreed, setAgreed] = useState(false);
  const cardOnFile = context === "clinic" ? clinic.cardOnFile : null;
  const book = useMutation({
    mutationFn: () =>
      post<OnlineConfirmation>(`${base}/bookings`, {
        tracking: trackingCookies(clinic.tracking),
        bookerClientId: bookerId,
        start: `${date}T${time}`,
        notes,
        context,
        occurrenceId,
        answers: Object.entries(answers).map(([label, answer]) => ({ label, answer })),
        mobile: place ? { address: place.address, locationType: place.locationType, acknowledged: true, noticeAgreed: place.noticeAgreed, outsideAgreed: place.outsideAgreed } : null,
        guests: guests.map((g) => ({ clientId: g.clientId, person: g.person, services: g.services, staffId: g.staffId })),
        agreeToFees: agreed,
        useCardOnFile: !!onFile && useOnFile,
        attemptId,
      }),
    onSuccess: onBooked,
    onError,
  });
  const prices = quote.data?.prices ?? [];
  const travel = place?.check.travelFeeCents ?? 0;
  // A voucher pays for the service; member pricing comes off at checkout.
  const guestTotal = (p: (typeof prices)[number]) => p.totalCents - p.services.reduce((t, sv) => t + (sv.coveredBy ? sv.priceCents : 0), 0) - (p.memberDiscountCents ?? 0);
  const total = prices.reduce((s, p) => s + guestTotal(p), 0) + travel;
  // Account credit (signed in, their household): goes toward each person's own visit at checkout.
  const creditLeft = new Map((clinic.me?.people ?? []).map((x) => [x.id, x.creditCents]));
  const credits = guests.map((g, i) => {
    const p = prices[i];
    if (!g.clientId || !p) return 0;
    const used = Math.max(0, Math.min(creditLeft.get(g.clientId) ?? 0, guestTotal(p)));
    creditLeft.set(g.clientId, (creditLeft.get(g.clientId) ?? 0) - used);
    return used;
  });
  const creditTotal = credits.reduce((s, c) => s + c, 0);
  const onFile = context === "clinic" ? (clinic.me?.people.find((x) => x.id === bookerId)?.card ?? null) : null;
  const [useOnFile, setUseOnFile] = useState(true);
  const addingCard = !!cardOnFile && !cardOnFile.optional && !(onFile && useOnFile);
  const name = (serviceId: string, fallback?: string) => menu.services.find((s) => s.id === serviceId)?.name ?? fallback;
  const missing = (event?.questions ?? []).some((q) => q.required && !answers[q.label]?.trim());
  // Deposits: mobile (held, charged when a nurse accepts) and events (paid now).
  const depositRule = context === "mobile" ? clinic.deposits.mobile : context === "event" ? (event?.depositPercent != null ? { mode: "percent" as const, flatCents: 0, percent: event.depositPercent, minCents: null, maxCents: null } : clinic.deposits.event) : null;
  const deposit = depositRule && !quote.isLoading ? depositFor(depositRule, guests.length, total) : 0;
  const needsAgreement = !!cardOnFile && cardOnFile.feeCents > 0;

  return (
    <div className="flex flex-col gap-4">
      <section className="rounded-[var(--radius-panel)] border border-line bg-white p-4">
        <p className="font-semibold">
          {dateLabel(date, { weekday: "long", month: "long", day: "numeric" })} at {time12(`${date}T${time}`)}
        </p>
        {place && <p className="mt-1 text-sm text-ink-soft">{[place.label, place.address.line2].filter(Boolean).join(", ")}</p>}
        {event && <p className="mt-1 text-sm text-ink-soft">{event.venue || event.name}</p>}
        <ul className="mt-3 flex flex-col gap-3">
          {guests.map((g, i) => {
            const p = prices[i];
            return (
              <li key={g.key} className="text-sm">
                <div className="font-semibold">{g.label}</div>
                {(p?.services ?? g.services.map((sv) => ({ serviceId: sv.serviceId, name: name(sv.serviceId) ?? "", listPriceCents: 0, discountCents: 0, priceCents: 0, addOns: [], coveredBy: null }))).map((sv) => (
                  <div key={sv.serviceId}>
                    <div className="flex justify-between gap-3">
                      <span>{name(sv.serviceId, sv.name)}</span>
                      {p &&
                        (sv.coveredBy ? (
                          <span className="tabular-nums">
                            <span className="mr-1.5 text-muted line-through">{formatCents(sv.listPriceCents)}</span>
                            {formatCents(0)}
                          </span>
                        ) : (
                          <span className="tabular-nums">{formatCents(sv.listPriceCents)}</span>
                        ))}
                    </div>
                    {sv.coveredBy && <div className="text-xs font-semibold text-ok">Covered by your {sv.coveredBy}</div>}
                    {sv.addOns.map((a) => (
                      <div key={a.id} className="flex justify-between gap-3 pl-3 text-ink-soft">
                        <span>+ {menu.addOns.find((x) => x.id === a.id)?.name ?? a.name}</span>
                        <span className="tabular-nums">{a.included ? "Included" : formatCents(a.priceCents)}</span>
                      </div>
                    ))}
                  </div>
                ))}
                {p && p.services.reduce((t, sv) => t + (sv.coveredBy ? 0 : sv.discountCents), 0) > 0 && (
                  <div className="flex justify-between gap-3 text-ok">
                    <span>Group discount</span>
                    <span className="tabular-nums">−{formatCents(p.services.reduce((t, sv) => t + (sv.coveredBy ? 0 : sv.discountCents), 0))}</span>
                  </div>
                )}
                {p && (p.memberDiscountCents ?? 0) > 0 && (
                  <div className="flex justify-between gap-3 text-ok">
                    <span>Member pricing{p.memberDiscountLabel ? ` (${p.memberDiscountLabel.replace(/ off$/, "")})` : ""}</span>
                    <span className="tabular-nums">−{formatCents(p.memberDiscountCents!)}</span>
                  </div>
                )}
                {credits[i]! > 0 && (
                  <div className="flex justify-between gap-3 text-ok">
                    <span>{g.clientId === bookerId ? "Your" : `${g.label}'s`} account credit (at checkout)</span>
                    <span className="tabular-nums">−{formatCents(credits[i]!)}</span>
                  </div>
                )}
              </li>
            );
          })}
          {travel > 0 && (
            <li className="flex justify-between gap-3 text-sm">
              <span>Travel fee</span>
              <span className="tabular-nums">{formatCents(travel)}</span>
            </li>
          )}
        </ul>
        <div className="mt-3 flex justify-between border-t border-line pt-3 font-semibold">
          <span>Total</span>
          <span className="tabular-nums">{quote.isLoading ? "…" : formatCents(total)}</span>
        </div>
        {creditTotal > 0 && !quote.isLoading && (
          <>
            <div className="mt-1 flex justify-between text-sm text-ok">
              <span>Account credit</span>
              <span className="tabular-nums">−{formatCents(creditTotal)}</span>
            </div>
            <div className="mt-1 flex justify-between font-semibold">
              <span>Estimated at checkout</span>
              <span className="tabular-nums">{formatCents(total - creditTotal)}</span>
            </div>
            <p className="mt-2 text-xs text-muted">Account credit comes off when you check out, not now. Tips are added at checkout.</p>
          </>
        )}
      </section>
      {guests.some((g) => g.person && !isMinor(g.person.dob)) && (
        <p className="text-sm text-ink-soft">
          {guests
            .filter((g) => g.person && !isMinor(g.person.dob))
            .map((g) => g.label)
            .join(" and ")}{" "}
          will get a text with a link to fill in their own forms.
        </p>
      )}
      {context === "clinic" && clinic.depositCents != null && !cardOnFile && (
        <p className="text-sm text-ink-soft">A {formatCents(clinic.depositCents)} deposit holds your appointment. It goes toward your total at the visit.</p>
      )}
      {deposit > 0 && context === "mobile" && (
        <p className="text-sm text-ink-soft">
          A {formatCents(deposit)} deposit is held on your card now and charged only when a nurse confirms your visit. It goes toward your total.
        </p>
      )}
      {deposit > 0 && context === "event" && <p className="text-sm text-ink-soft">A {formatCents(deposit)} deposit holds your spot. It goes toward your total at the event.</p>}
      {cardOnFile?.optional && needsAgreement && (
        <div className="rounded-[var(--radius-control)] border border-line bg-white px-3 py-2">
          <Checkbox label={cardOnFile.agreement} checked={agreed} onChange={setAgreed} />
        </div>
      )}
      {cardOnFile && !cardOnFile.optional && (
        <div className="flex flex-col gap-1 rounded-[var(--radius-control)] border border-line bg-white px-3 py-2">
          {onFile ? (
            <fieldset className="flex flex-col gap-1 text-sm">
              <legend className="mb-1 font-semibold">Card for this visit (nothing is charged now)</legend>
              <label className="flex items-center gap-2 py-1">
                <input type="radio" name="card" className="accent-[var(--color-brand)]" checked={useOnFile} onChange={() => setUseOnFile(true)} />
                Use my card on file: {onFile.brand === "link" ? "saved with Link" : `${onFile.brand.toUpperCase()} •••• ${onFile.last4}`}
              </label>
              <label className="flex items-center gap-2 py-1">
                <input type="radio" name="card" className="accent-[var(--color-brand)]" checked={!useOnFile} onChange={() => setUseOnFile(false)} />
                Use a different card (next step)
              </label>
            </fieldset>
          ) : (
            <p className="text-sm text-ink-soft">You'll add a card on the next step. Nothing is charged now.</p>
          )}
          {needsAgreement && <Checkbox label={cardOnFile.agreement} checked={agreed} onChange={setAgreed} />}
        </div>
      )}
      {context === "mobile" && clinic.mobile?.requiresConfirmation && (
        <Alert tone="info">This is a request. A nurse will confirm it, usually within the hour, and we'll text you.</Alert>
      )}
      <div className="rounded-[var(--radius-control)] bg-mist px-4 py-3 text-sm text-ink-soft">
        <p className="font-semibold text-ink">Cancellation policy</p>
        <p className="mt-1 whitespace-pre-line">{clinic.cancellation.policy}</p>
      </div>
      {event?.questions.map((q) => (
        <Field key={q.label} label={`${q.label}${q.required ? "" : " (optional)"}`} required={q.required} maxLength={300} value={answers[q.label] ?? ""} onChange={(e) => setAnswers({ ...answers, [q.label]: e.target.value })} />
      ))}
      <Field label="Anything we should know? (optional)" maxLength={500} value={notes} onChange={(e) => setNotes(e.target.value)} />
      {!!book.error && <Alert>{errorMessage(book.error)}</Alert>}
      <Button variant="primary" className="py-3" busy={book.isPending} disabled={missing || (needsAgreement && !agreed)} onClick={() => book.mutate()}>
        {addingCard ? "Continue: add a card" : deposit > 0 ? `Continue: ${context === "mobile" ? "hold" : "pay"} the deposit` : context === "mobile" && clinic.mobile?.requiresConfirmation ? "Send request" : `Book ${guests.length > 1 ? `for ${guests.length}` : "it"}`}
      </Button>
    </div>
  );
}

function DoneStep({
  base,
  clinic,
  c,
  context,
  place,
  names,
  share,
  onAnother,
}: {
  base: string;
  clinic: OnlineClinic;
  c: OnlineConfirmation;
  context: Context;
  place: Place | null;
  /** Guest → the service name they saw (events can rename services). */
  names: Map<string, string>;
  /** Guests (by position) who fill in their own forms: the booker can pass the link on, not open it. */
  share: Set<number>;
  onAnother: () => void;
}) {
  const [shared, setShared] = useState<number | null>(null);
  // On the lobby display: it clears this screen after a moment.
  useEffect(() => {
    if (inLobby) window.parent.postMessage("vaulted-lobby-done", window.location.origin);
  }, []);
  // The forms open in their own tab: coming back here checks what's still left.
  const wanted = c.guests.some((g) => g.formsNeeded > 0 || g.screening);
  const live = useQuery({
    queryKey: ["booking-forms", c.reference],
    queryFn: () => get<{ forms?: { appointmentId: string; formsNeeded: number; screening: boolean }[] }>(`${base}/bookings/${c.reference}/status`),
    enabled: wanted,
    refetchOnWindowFocus: true,
    refetchInterval: 30_000,
  });
  const guests = c.guests.map((g) => {
    const f = live.data?.forms?.find((x) => x.appointmentId === g.appointmentId);
    return f ? { ...g, formsNeeded: f.formsNeeded, screening: f.screening } : g;
  });
  const allDone = wanted && !guests.some((g) => g.formsNeeded > 0 || g.screening);
  const shareLink = async (i: number, name: string, link: string) => {
    const text = `Your forms for ${clinic.clinic.name}: ${link}`;
    try {
      if (navigator.share) await navigator.share({ title: `${name}'s forms`, text });
      else await navigator.clipboard?.writeText(text);
      setShared(i);
    } catch {
      // They closed the share sheet.
    }
  };
  const [qr, setQr] = useState<string | null>(null);
  useEffect(() => {
    if (context === "event") void QRCode.toDataURL(c.reference, { margin: 1, width: 200 }).then(setQr);
  }, [c.reference, context]);
  return (
    <div className="flex flex-col gap-4">
      <Alert tone={c.status === "requested" ? "info" : "ok"}>
        {c.status === "requested" ? "Request" : "Confirmation"} <span className="font-semibold tracking-wider">{c.reference}</span>
      </Alert>
      {c.status === "requested" && <p className="text-ink-soft">A nurse will look at your request and confirm it. We'll text you as soon as they do.</p>}
      <section className="rounded-[var(--radius-panel)] border border-line bg-white p-4">
        <p className="font-semibold">{dateLabel(c.start.slice(0, 10), { weekday: "long", month: "long", day: "numeric" })}</p>
        {place && <p className="text-sm text-ink-soft">{place.label}</p>}
        <ul className="mt-2 flex flex-col gap-1.5 text-sm">
          {c.guests.map((g, i) => (
            <li key={i}>
              <span className="tabular-nums">{time12(g.start)}</span> · <span className="font-semibold">{g.name}</span>: {names.get(g.name) || g.serviceName}
              {g.addOns.length > 0 && ` + ${g.addOns.join(", ")}`}
              {g.coveredBy && <span className="block text-xs font-semibold text-ok">Covered by your {g.coveredBy}</span>}
            </li>
          ))}
        </ul>
        {c.travelFeeCents > 0 && <p className="mt-2 text-sm">Travel fee {formatCents(c.travelFeeCents)}</p>}
        <p className="mt-3 text-sm font-semibold">Total {formatCents(c.totalCents)}</p>
        {c.guests.some((g) => g.creditCents > 0) && (
          <p className="mt-1 text-sm text-ok">
            {formatCents(c.guests.reduce((s, g) => s + g.creditCents, 0))} of account credit will be applied at checkout (estimated {formatCents(c.totalCents - c.guests.reduce((s, g) => s + g.creditCents, 0))}).
          </p>
        )}
      </section>
      {guests.some((g) => g.formsNeeded > 0 || g.screening) && (
        <section className="flex flex-col gap-3 rounded-[var(--radius-panel)] border-2 border-brand bg-white p-4">
          <h2 className="font-display text-xl">Next: your forms{guests.some((g) => g.screening) ? " and health screening" : ""}</h2>
          <p className="text-sm text-ink-soft">
            {guests.some((g) => g.formsNeeded > 0) ? "A short intake and our consent, about 5 minutes. " : ""}
            {guests.some((g) => g.screening) ? "Then a quick good faith exam with a licensed provider: a short video call, with your intake answers already filled in. " : ""}
            Doing it now saves time when you arrive.{guests.length > 1 ? " Each guest has their own link, opened with their date of birth." : ""}
          </p>
          <div className="flex flex-col gap-2">
            {guests.map((g, i) =>
              wanted && c.guests[i] && (c.guests[i]!.formsNeeded > 0 || c.guests[i]!.screening) && !(g.formsNeeded > 0 || g.screening) ? (
                <p key={i} className="text-sm font-semibold text-ok">
                  {guests.length > 1 ? `${g.name}: forms done ✓` : "Forms done ✓"}
                </p>
              ) : inLobby ? null : share.has(i) && (g.formsNeeded > 0 || g.screening) ? (
                <div key={i} className="flex flex-col gap-1 rounded-[var(--radius-control)] border border-line px-3 py-2">
                  <span className="text-sm">
                    <span className="font-semibold">{g.name}</span> gets their own text with their forms link. They'll need their date of birth to open it.
                  </span>
                  <button type="button" className="self-start text-sm font-semibold text-brand" onClick={() => void shareLink(i, g.name, g.formsLink)}>
                    {shared === i ? "Shared ✓" : `Share ${g.name}'s link`}
                  </button>
                </div>
              ) : g.formsNeeded > 0 || g.screening ? (
                <a
                  key={i}
                  href={g.formsLink}
                  target="_blank"
                  rel="noreferrer"
                  className={cx(
                    "inline-flex min-h-11 items-center justify-center rounded-[var(--radius-control)] px-4 font-semibold",
                    i === 0 ? "bg-brand text-white hover:bg-brand-deep" : "border border-brand text-brand hover:bg-brand-wash",
                  )}
                >
                  {guests.length > 1 ? `Start ${g.name}'s` : g.formsNeeded > 0 ? "Fill out my forms now" : "Start my health screening"}
                  {guests.length > 1 ? (g.formsNeeded > 0 ? " forms" : " health screening") : ""}
                </a>
              ) : null,
            )}
          </div>
          <p className="text-xs text-muted">{inLobby ? `We'll text you the link${guests.length > 1 ? "s" : ""} to fill out on your phone.` : `Not now? We'll text you the link${guests.length > 1 ? "s" : ""}.`}</p>
        </section>
      )}
      {allDone && <Alert tone="ok">Forms done. You're all set for your visit.</Alert>}
      {c.addCardLink && !inLobby && (
        <details className="rounded-[var(--radius-control)] border border-line bg-white px-3 py-2 text-sm">
          <summary className="cursor-pointer font-semibold">Optionally add a card on file for faster checkout</summary>
          <p className="mt-1 text-ink-soft">Nothing is charged now. We'll use it at checkout so you're out the door faster.</p>
          <a href={c.addCardLink} target="_blank" rel="noreferrer" className="mt-2 inline-flex min-h-10 items-center rounded-[var(--radius-control)] bg-brand px-4 font-semibold text-white hover:bg-brand-deep">
            Add a card
          </a>
        </details>
      )}
      {qr && (
        <div className="flex flex-col items-center gap-1 rounded-[var(--radius-panel)] border border-line bg-white p-4">
          <img src={qr} alt={`Check-in code ${c.reference}`} className="size-40" />
          <p className="text-sm text-muted">Show this at check-in.</p>
        </div>
      )}
      {context === "clinic" && c.status === "confirmed" && clinic.confirmByText && (
        <Alert tone="info">We just texted you the details. Reply C to that text to confirm your visit.</Alert>
      )}
      <p className="text-sm text-ink-soft">
        Need to change it? {clinic.clinic.phone ? `Call us at ${clinic.clinic.phone}.` : "Give us a call."} Please let us know at least {clinic.cancellation.windowHours} hours ahead.
      </p>
      <Button onClick={onAnother}>Book another visit</Button>
    </div>
  );
}

/** The card or deposit step after booking: Stripe's page, then wait for Stripe to tell us it went through. */
function PayStep({ base, c, onPaid }: { base: string; c: OnlineConfirmation; onPaid: (status: OnlineConfirmation["status"], depositStatus: OnlineConfirmation["depositStatus"]) => void }) {
  const [finished, setFinished] = useState(false);
  const status = useQuery({
    queryKey: ["book-status", c.reference],
    queryFn: () => get<{ status: OnlineConfirmation["status"] | "expired" | "declined"; depositStatus: OnlineConfirmation["depositStatus"] }>(`${base}/bookings/${c.reference}/status`),
    enabled: finished,
    refetchInterval: (q) => (q.state.data && q.state.data.status !== "pending" ? false : 1500),
  });
  const s = status.data;
  useEffect(() => {
    if (s && (s.status === "confirmed" || s.status === "requested")) onPaid(s.status, s.depositStatus);
  }, [s, onPaid]);
  const p = c.payment!;
  return (
    <div className="flex flex-col gap-4">
      <div>
        <h2 className="font-display text-xl">{p.kind === "save_card" ? "Add a card for your visit" : p.kind === "deposit_hold" ? `Hold the ${formatCents(p.amountCents)} deposit` : `Pay the ${formatCents(p.amountCents)} deposit`}</h2>
        <p className="text-sm text-ink-soft">
          {p.kind === "save_card"
            ? "Your card is saved for your visit. Nothing is charged now."
            : p.kind === "deposit_hold"
              ? "It's held on your card and charged only when a nurse confirms your visit."
              : "It goes toward your total at the event."}{" "}
          Your time is held for 30 minutes.
        </p>
      </div>
      {s?.status === "expired" ? (
        <Alert>This booking timed out before the card was added. Please book again.</Alert>
      ) : finished ? (
        <div className="flex items-center gap-3 text-ink-soft">
          <span className="size-4 animate-spin rounded-full border-2 border-brand border-r-transparent" aria-hidden /> Finishing your booking…
        </div>
      ) : (
        <StripeCheckout session={p.session} onComplete={() => setFinished(true)} testLabel={p.kind === "save_card" ? "Save a test card" : "Complete test payment"} />
      )}
    </div>
  );
}

type PlanKindOnline = "package" | "membership";

/** Packages and memberships, as choices beside the treatment categories. */
const PLAN_GROUPS: { kind: PlanKindOnline; heading: string; detail: string; action: string; per: boolean }[] = [
  { kind: "package", heading: "Packages", detail: "Prepaid visits at a better price.", action: "Buy", per: false },
  { kind: "membership", heading: "Memberships", detail: "Monthly plans with visits and savings.", action: "Join", per: true },
];

/** The packages or memberships sold online. */
function PlanOffers({ slug, clinic, kind }: { slug: string; clinic: OnlineClinic; kind: PlanKindOnline }) {
  const people = clinic.me?.people ?? [];
  const groups = PLAN_GROUPS.filter((g) => g.kind === kind)
    .map((g) => ({ ...g, plans: clinic.plans.filter((p) => p.kind === g.kind) }))
    .filter((g) => g.plans.length);
  if (!groups.length) return <Alert tone="info">None offered online right now.</Alert>;
  return (
    <div className="flex flex-col gap-3">
      {groups.map((g) => (
        <section key={g.kind} className="flex flex-col gap-3">
          {g.plans.map((p) => {
            const owners = people.filter((x) => x.planIds.includes(p.id));
            return (
              <div key={p.id} className="flex flex-col gap-2 rounded-[var(--radius-panel)] border border-line bg-white p-4">
                <div>
                  <h3 className="font-semibold">{p.name}</h3>
                  <p className="font-semibold tabular-nums">
                    {formatCents(p.priceCents)}
                    {g.per && <span className="text-sm font-normal text-muted"> per month</span>}
                  </p>
                </div>
                {p.description && <p className="text-sm text-ink-soft">{p.description}</p>}
                {owners.length > 0 && (
                  <p className="text-sm font-semibold text-ok">
                    {owners.map((o) => o.firstName).join(" and ")} {g.kind === "membership" ? (owners.length > 1 ? "are members" : "is a member") : owners.length > 1 ? "have this package" : "has this package"}.
                  </p>
                )}
                <Link
                  to={`/join/${slug}/${p.id}`}
                  className="self-start rounded-[var(--radius-control)] bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-deep"
                >
                  {owners.length && g.kind === "membership" ? "Details" : g.action}
                </Link>
              </div>
            );
          })}
        </section>
      ))}
    </div>
  );
}
